import { IMenu, ISubMenuGroup } from 'apis/types';
import { DishMenuGroupDetail, DishMenuGroupDetailValue, PortionState, PortionStateItem } from 'models';
import { getDishInfo } from 'utils/getDishInfo';
import { SubmenuGroupInfoType } from '../apis/types';

/**
 * Initializes the state for a dish based on the provided item information, menu list, and sub-menu group.
 *
 * @param menuList - The menu list containing dish information.
 * @param subMenuGroupData - The sub-menu group containing basic information and sub-menu details.
 * @param submenuGroupInfo - The sub-menu group information.
 * @param customerQuantity - The number of customers.
 * @param addDefaultValue - A boolean flag indicating whether to add default values to the state. Defaults to `true`.
 * @returns An array of initial state objects, where each object represents a portion state for a specific sub-menu group.
 */
export const initDishState = (
  menuList: IMenu,
  subMenuGroupData: ISubMenuGroup,
  submenuGroupInfo: SubmenuGroupInfoType[],
  customerQuantity: number,
  addDefaultValue = true
): PortionState[] => {
  const initState: PortionState = {};

  const subMenuGroupCodesValid = submenuGroupInfo
    .filter((group) => group?.submenu.length && subMenuGroupData[group.submenugroup_cd]?.submenu.length > 0)
    .map((group) => group.submenugroup_cd);

  subMenuGroupCodesValid.forEach((subMenuGroupCode) => {
    const subMenuGroupItem = subMenuGroupData[subMenuGroupCode];
    if (subMenuGroupItem) {
      const noselectFlg = subMenuGroupItem.noselectflg;
      const defaultSettingFlg = subMenuGroupItem.default_setting_flg;

      const submenuGroup = submenuGroupInfo?.find((group) => group.submenugroup_cd === subMenuGroupCode);
      const submenuValid = submenuGroup?.submenu;
      const submenuData = subMenuGroupItem?.submenu;
      const submenu = submenuValid
        ?.map((validItem) => {
          const matchItem = submenuData.find((d) => d.poscode === validItem.poscd);
          return matchItem ? matchItem : null;
        })
        .filter(Boolean) as typeof submenuData;
      const minDefaultPriority = Math.min(...submenu.map((item) => Number(item.default_priority)));
      const defaultForSingle = subMenuGroupItem.defaultforsingle;
      const selectedDishCode: string[] = [];
      const detailList: DishMenuGroupDetail = {};

      submenu.forEach((item) => {
        const poscode = item.poscode;
        const submenuDataDetail = submenuValid?.find((item) => item.poscd === poscode);
        const optionInfo = getDishInfo(menuList, poscode);
        const defaultPriority = Number(item.default_priority);

        const isChecked = addDefaultValue
          ? noselectFlg === '1' ||
            (customerQuantity === 1 && defaultForSingle === '1' && defaultPriority === minDefaultPriority) ||
            (defaultSettingFlg === '1' && defaultPriority === minDefaultPriority)
          : false;

        const detailItem: DishMenuGroupDetailValue = {
          optionInfo: optionInfo,
          checked: isChecked,
          quantity: 1,
        };

        detailList[poscode] = detailItem;

        if (isChecked) {
          selectedDishCode.push(poscode);
        }

        if (submenuDataDetail && submenuDataDetail.submenugroup && submenuDataDetail.submenugroup.length > 0) {
          const listChildOptionGroups: PortionState = {};

          const childSubmenuGroupInfo = submenuDataDetail.submenugroup;
          const childSubmenuGroupCodesValid = childSubmenuGroupInfo
            .filter((group) => group?.submenu.length && subMenuGroupData[group.submenugroup_cd]?.submenu.length > 0)
            .map((group) => group.submenugroup_cd);

          childSubmenuGroupCodesValid.forEach((childSubmenuGroupCode) => {
            const childSubmenuGroupItem = subMenuGroupData[childSubmenuGroupCode];

            if (childSubmenuGroupItem) {
              const childNoSelectFlg = childSubmenuGroupItem.noselectflg;

              const childDefaultSettingFlg = childSubmenuGroupItem.default_setting_flg;

              const childSubmenuGroup = childSubmenuGroupInfo?.find(
                (group) => group.submenugroup_cd === childSubmenuGroupCode
              );

              const childSubmenuValid = childSubmenuGroup?.submenu;
              const childSubmenuData = childSubmenuGroupItem?.submenu;
              const childSubmenu = childSubmenuValid
                ?.map((validItem) => {
                  const matchItem = childSubmenuData.find((d) => d.poscode === validItem.poscd);
                  return matchItem ? matchItem : null;
                })
                .filter(Boolean) as typeof childSubmenuData;

              const childMinDefaultPriority = Math.min(...childSubmenu.map((item) => Number(item.default_priority)));
              const childDefaultForSingle = childSubmenuGroupItem.defaultforsingle;

              const childSelectedDishCode: string[] = [];
              const childDetailList: DishMenuGroupDetail = {};

              childSubmenu.forEach((childSubmenuItem) => {
                const childPoscode = childSubmenuItem.poscode;
                const childOptionInfo = getDishInfo(menuList, childPoscode);
                const childDefaultPriority = Number(childSubmenuItem.default_priority);
                const childIsChecked = addDefaultValue
                  ? childNoSelectFlg === '1' ||
                    (isChecked &&
                      ((customerQuantity === 1 &&
                        childDefaultForSingle === '1' &&
                        childDefaultPriority === childMinDefaultPriority) ||
                        (childDefaultSettingFlg === '1' && childDefaultPriority === childMinDefaultPriority)))
                  : false;

                const childDetailItem: DishMenuGroupDetailValue = {
                  optionInfo: childOptionInfo,
                  checked: childIsChecked,
                  quantity: 1,
                };

                childDetailList[childPoscode] = childDetailItem;

                if (childIsChecked) {
                  childSelectedDishCode.push(childPoscode);
                }
              });

              if (Object.keys(childDetailList).length > 0) {
                const childOptionGroup: PortionStateItem = {
                  basic: { ...childSubmenuGroupItem, submenu: childSubmenu },
                  detail: childDetailList,
                  selected: childSelectedDishCode,
                };

                listChildOptionGroups[`${childSubmenuGroupCode}-0`] = childOptionGroup;
              }
            }
          });

          detailItem.childInfor = listChildOptionGroups;
        }
      });

      const optionGroup: PortionStateItem = {
        basic: subMenuGroupItem,
        detail: detailList,
        selected: selectedDishCode,
      };

      initState[`${subMenuGroupCode}-0`] = optionGroup;
    }
  });

  return [initState];
};
